import { useState, useEffect } from 'react'
import { useStore } from '../store'
import api from '../services/api'
import { Plus, Trash2, ChevronDown, ChevronRight, Cpu, BookOpen, ChevronUp } from 'lucide-react'

interface SidebarProps {
  onNavigate: (page: 'chat' | 'skills' | 'knowledge' | 'stats') => void
  currentPage: string
}

interface ConversationGroup {
  model: string
  color: string
  conversations: any[]
  expanded: boolean
}

export default function Sidebar({ currentPage }: SidebarProps) {
  const {
    conversations,
    currentConversation,
    messages,
    skills,
    projects,
    activeProjectId,
    fetchConversations,
    fetchSkills,
    fetchProjects,
    createConversation,
    selectConversation,
    deleteConversation,
    setSelectedProviderId,
    setSelectedSkillId,
    setActiveProject,
    selectedProviderId,
    selectedSkillId
  } = useStore()

  const [groups, setGroups] = useState<Map<string, ConversationGroup>>(new Map())
  const [providers, setProviders] = useState<any[]>([])
  const [expandedModels, setExpandedModels] = useState<Set<string>>(new Set(['Ollama (Local)']))
  const [showRoles, setShowRoles] = useState(false)
  const [showNewProjectForm, setShowNewProjectForm] = useState(false)
  const [newProject, setNewProject] = useState({ name: '', icon: '📁', color: '#6366f1' })
  const [hoveredProjectId, setHoveredProjectId] = useState<string | null>(null)
  const [editingProjectId, setEditingProjectId] = useState<string | null>(null)
  const [renamingProjectName, setRenamingProjectName] = useState('')
  const [showNewSkillForm, setShowNewSkillForm] = useState(false)
  const [newSkill, setNewSkill] = useState({ name: '', content: '' })
  const [hoveredSkillId, setHoveredSkillId] = useState<string | null>(null)
  const [editingSkillId, setEditingSkillId] = useState<string | null>(null)
  const [editingSkill, setEditingSkill] = useState({ name: '', content: '' })

  useEffect(() => {
    if (currentPage === 'chat') {
      fetchConversations()
      fetchSkills()
      fetchProjects()
    }
  }, [currentPage, fetchConversations, fetchSkills, fetchProjects])

  useEffect(() => {
    const fetchProviders = async () => {
      try {
        const response = await api.get('/ai-providers/')
        setProviders(response.data || [])
        if (response.data?.length > 0 && !selectedProviderId) {
          setSelectedProviderId(String(response.data[0].id))
        }
      } catch (error) {
        console.error('Erreur chargement providers:', error)
      }
    }
    fetchProviders()
  }, [])

  // Grouper conversations par modèle (filtré par projet actif)
  useEffect(() => {
    const groupMap = new Map<string, ConversationGroup>()
    const filteredConvs = conversations.filter((c: any) => (c.project_id || 'general') === activeProjectId)
    filteredConvs.forEach(conv => {
      const convMessages = messages.filter(m => m.conversation_id === conv.id)
      const lastAssistantMsg = [...convMessages].reverse().find(m => m.role === 'assistant' && m.model_name)
      const modelName = lastAssistantMsg?.model_name || 'Ollama (Local)'
      const color = getModelColor(modelName)
      if (!groupMap.has(modelName)) {
        groupMap.set(modelName, { model: modelName, color, conversations: [], expanded: expandedModels.has(modelName) })
      }
      groupMap.get(modelName)!.conversations.push(conv)
    })
    groupMap.forEach(group => {
      group.conversations.sort((a, b) => new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime())
    })
    setGroups(groupMap)
  }, [conversations, messages, activeProjectId])

  const getModelColor = (modelName: string): string => {
    if (modelName.includes('qwen')) return '#3b82f6'
    if (modelName.includes('claude')) return '#8b5cf6'
    if (modelName.includes('gpt')) return '#10b981'
    if (modelName.includes('gemini')) return '#f59e0b'
    return '#6366f1'
  }

  const toggleModelGroup = (modelName: string) => {
    const newExpanded = new Set(expandedModels)
    if (newExpanded.has(modelName)) newExpanded.delete(modelName)
    else newExpanded.add(modelName)
    setExpandedModels(newExpanded)
  }

  const handleNewConversation = async () => {
    const conv = await createConversation('Nouvelle conversation', 'default')
    await selectConversation(conv.id)
  }

  const handleCreateProject = async () => {
    if (!newProject.name.trim()) return
    try {
      await api.post('/api/projects/', newProject)
      setShowNewProjectForm(false)
      setNewProject({ name: '', icon: '📁', color: '#6366f1' })
      await fetchProjects()
    } catch (error) {
      console.error('Erreur création projet:', error)
    }
  }

  const handleDeleteConversation = async (e: React.MouseEvent, id: string) => {
    e.stopPropagation()
    await deleteConversation(id)
  }

  const handleRenameProject = async (projectId: string) => {
    if (!renamingProjectName.trim()) return
    try {
      await api.put(`/api/projects/${projectId}`, { name: renamingProjectName })
      setEditingProjectId(null)
      setRenamingProjectName('')
      await fetchProjects()
    } catch (error) {
      console.error('Erreur renommage projet:', error)
    }
  }

  const handleDeleteProject = async (projectId: string) => {
    if (projectId === 'general') return
    const confirmed = window.confirm('Supprimer ce projet ? Les conversations et skills ne seront PAS supprimés, juste déplacés vers Général.')
    if (!confirmed) return
    try {
      await api.delete(`/api/projects/${projectId}`)
      if (activeProjectId === projectId) {
        setActiveProject('general')
      }
      await fetchProjects()
    } catch (error) {
      console.error('Erreur suppression projet:', error)
    }
  }

  const startRenaming = (e: React.MouseEvent, project: any) => {
    e.stopPropagation()
    setEditingProjectId(project.id)
    setRenamingProjectName(project.name)
  }

  const handleCreateSkill = async () => {
    if (!newSkill.name.trim() || !newSkill.content.trim()) return
    try {
      await api.post('/api/skills/', {
        name: newSkill.name,
        content: newSkill.content,
        type: 'prompt',
        project_id: activeProjectId,
        description: '',
        tags: []
      })
      setShowNewSkillForm(false)
      setNewSkill({ name: '', content: '' })
      await fetchSkills()
    } catch (error) {
      console.error('Erreur création skill:', error)
    }
  }

  const startEditingSkill = (e: React.MouseEvent, skill: any) => {
    e.stopPropagation()
    setEditingSkillId(skill.id)
    setEditingSkill({ name: skill.name, content: skill.content })
  }

  const handleUpdateSkill = async (skillId: string) => {
    if (!editingSkill.name.trim() || !editingSkill.content.trim()) return
    try {
      await api.put(`/api/skills/${skillId}`, {
        name: editingSkill.name,
        content: editingSkill.content
      })
      setEditingSkillId(null)
      setEditingSkill({ name: '', content: '' })
      await fetchSkills()
    } catch (error) {
      console.error('Erreur mise à jour skill:', error)
    }
  }

  const handleDeleteSkill = async (e: React.MouseEvent, skillId: string) => {
    e.stopPropagation()
    const confirmed = window.confirm('Supprimer ce rôle/prompt ?')
    if (!confirmed) return
    try {
      await api.delete(`/api/skills/${skillId}`)
      if (selectedSkillId === skillId) {
        setSelectedSkillId(null)
      }
      await fetchSkills()
    } catch (error) {
      console.error('Erreur suppression skill:', error)
    }
  }

  if (currentPage !== 'chat') return null

  return (
    <aside style={{
      backgroundColor: '#1f2937',
      width: '256px',
      minWidth: '256px',
      borderRight: '1px solid #374151',
      display: 'flex',
      flexDirection: 'column',
      overflow: 'hidden'
    }}>

      {/* ── Header ── */}
      <div style={{ borderBottom: '1px solid #374151', padding: '14px', flexShrink: 0 }}>
        <button
          onClick={handleNewConversation}
          style={{
            width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center',
            gap: '8px', backgroundColor: '#ec4899', color: '#ffffff', border: 'none',
            borderRadius: '8px', padding: '9px', cursor: 'pointer', fontSize: '13px',
            fontWeight: '600', transition: 'all 0.2s'
          }}
          onMouseEnter={e => e.currentTarget.style.backgroundColor = '#db2777'}
          onMouseLeave={e => e.currentTarget.style.backgroundColor = '#ec4899'}
        >
          <Plus size={16} /> Nouvelle discussion
        </button>
      </div>

      {/* ── Contenu scrollable ── */}
      <div style={{ flex: 1, overflowY: 'auto', display: 'flex', flexDirection: 'column' }}>

        {/* ── Section PROJETS ── */}
        <div style={{ borderBottom: '1px solid #374151', flexShrink: 0, padding: '10px 8px' }}>
          <div style={{ fontSize: '11px', color: '#6b7280', fontWeight: '600', letterSpacing: '0.08em', textTransform: 'uppercase', padding: '0 6px 8px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>📁 PROJETS</span>
            <button
              onClick={() => setShowNewProjectForm(!showNewProjectForm)}
              style={{
                background: 'none', border: 'none', color: '#9ca3af', cursor: 'pointer',
                fontSize: '16px', padding: '0', lineHeight: 1, transition: 'color 0.15s'
              }}
              onMouseEnter={e => e.currentTarget.style.color = '#ec4899'}
              onMouseLeave={e => e.currentTarget.style.color = '#9ca3af'}
              title="Nouveau projet"
            >
              <Plus size={14} />
            </button>
          </div>

          {showNewProjectForm && (
            <div style={{ background: '#0f172a', border: '1px solid #374151', borderRadius: '8px', padding: '12px', marginBottom: '8px' }}>
              <input
                type="text"
                autoComplete="off"
                placeholder="Nom du projet *"
                value={newProject.name}
                onChange={e => setNewProject({...newProject, name: e.target.value})}
                style={{
                  width: '100%', padding: '6px 8px', background: '#1f2937', border: '1px solid #374151',
                  borderRadius: '6px', color: 'white', fontSize: '12px', marginBottom: '8px', boxSizing: 'border-box' as const
                }}
              />
              <div style={{ display: 'flex', gap: '8px', marginBottom: '8px' }}>
                <input
                  type="text"
                  autoComplete="off"
                  placeholder="Emoji"
                  value={newProject.icon}
                  onChange={e => setNewProject({...newProject, icon: e.target.value})}
                  style={{
                    width: '50px', padding: '6px 8px', background: '#1f2937', border: '1px solid #374151',
                    borderRadius: '6px', color: 'white', fontSize: '16px', textAlign: 'center', boxSizing: 'border-box' as const
                  }}
                />
                <input
                  type="color"
                  value={newProject.color}
                  onChange={e => setNewProject({...newProject, color: e.target.value})}
                  style={{
                    width: '50px', height: '32px', background: '#1f2937', border: '1px solid #374151',
                    borderRadius: '6px', cursor: 'pointer', boxSizing: 'border-box' as const
                  }}
                />
              </div>
              <div style={{ display: 'flex', gap: '6px' }}>
                <button
                  onClick={handleCreateProject}
                  disabled={!newProject.name.trim()}
                  style={{
                    flex: 1, padding: '6px', background: !newProject.name.trim() ? '#374151' : 'linear-gradient(to right, #ec4899, #a855f7)',
                    color: 'white', border: 'none', borderRadius: '6px', fontSize: '12px',
                    fontWeight: '600', cursor: !newProject.name.trim() ? 'not-allowed' : 'pointer'
                  }}
                >
                  ✓ Créer
                </button>
                <button
                  onClick={() => {
                    setShowNewProjectForm(false)
                    setNewProject({ name: '', icon: '📁', color: '#6366f1' })
                  }}
                  style={{
                    padding: '6px 12px', background: '#374151', color: 'white', border: 'none',
                    borderRadius: '6px', fontSize: '12px', cursor: 'pointer'
                  }}
                >
                  ✕
                </button>
              </div>
            </div>
          )}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '3px' }}>
            {projects.map(project => {
              const isEditing = editingProjectId === project.id
              const isHovered = hoveredProjectId === project.id
              const isActive = activeProjectId === project.id

              if (isEditing) {
                return (
                  <div
                    key={project.id}
                    style={{
                      display: 'flex', alignItems: 'center', gap: '4px',
                      padding: '6px 8px', borderRadius: '6px',
                      backgroundColor: '#374151', border: '1px solid #4b5563'
                    }}
                  >
                    <span style={{ fontSize: '14px' }}>{project.icon}</span>
                    <input
                      type="text"
                      autoComplete="off"
                      value={renamingProjectName}
                      onChange={e => setRenamingProjectName(e.target.value)}
                      onKeyDown={e => {
                        if (e.key === 'Enter') handleRenameProject(project.id)
                        if (e.key === 'Escape') { setEditingProjectId(null); setRenamingProjectName('') }
                      }}
                      autoFocus
                      style={{
                        flex: 1, padding: '4px 6px', fontSize: '12px', background: '#1f2937',
                        border: '1px solid #6b7280', borderRadius: '4px', color: 'white',
                        outline: 'none', boxSizing: 'border-box' as const
                      }}
                    />
                    <button
                      onClick={() => handleRenameProject(project.id)}
                      style={{
                        padding: '4px 6px', fontSize: '10px', background: '#10b981',
                        color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer'
                      }}
                    >
                      ✓
                    </button>
                    <button
                      onClick={() => { setEditingProjectId(null); setRenamingProjectName('') }}
                      style={{
                        padding: '4px 6px', fontSize: '10px', background: '#ef4444',
                        color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer'
                      }}
                    >
                      ✕
                    </button>
                  </div>
                )
              }

              return (
                <div
                  key={project.id}
                  style={{ position: 'relative' }}
                  onMouseEnter={() => setHoveredProjectId(project.id)}
                  onMouseLeave={() => setHoveredProjectId(null)}
                >
                  <button
                    onClick={() => setActiveProject(project.id)}
                    style={{
                      width: '100%', textAlign: 'left', padding: '8px 10px', borderRadius: '6px',
                      border: '1px solid', cursor: 'pointer', fontSize: '12px', transition: 'all 0.15s',
                      backgroundColor: isActive ? `${project.color}22` : (isHovered ? '#374151' : 'transparent'),
                      borderColor: isActive ? project.color : 'transparent',
                      color: isActive ? project.color : (isHovered ? '#f3f4f6' : '#9ca3af'),
                      display: 'flex', alignItems: 'center', gap: '8px', fontWeight: '600'
                    }}
                  >
                    <span style={{ fontSize: '16px' }}>{project.icon}</span>
                    <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{project.name}</span>
                    {isHovered && (
                      <div style={{ display: 'flex', gap: '4px', marginLeft: 'auto' }} onClick={e => e.stopPropagation()}>
                        <button
                          onClick={(e) => startRenaming(e, project)}
                          style={{
                            padding: '2px 6px', fontSize: '11px', background: 'transparent',
                            color: '#f59e0b', border: 'none', cursor: 'pointer', borderRadius: '4px'
                          }}
                          title="Renommer"
                        >
                          ✏️
                        </button>
                        {project.id !== 'general' && (
                          <button
                            onClick={(e) => { e.stopPropagation(); handleDeleteProject(project.id) }}
                            style={{
                              padding: '2px 6px', fontSize: '11px', background: 'transparent',
                              color: '#ef4444', border: 'none', cursor: 'pointer', borderRadius: '4px'
                            }}
                            title="Supprimer"
                          >
                            🗑️
                          </button>
                        )}
                      </div>
                    )}
                  </button>
                </div>
              )
            })}
          </div>
        </div>

        {/* ── Section Rôles & Prompts ── */}
        <div style={{ borderBottom: '1px solid #374151', flexShrink: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 14px' }}>
            <button
              onClick={() => setShowRoles(!showRoles)}
              style={{
                flex: 1, display: 'flex', alignItems: 'center', gap: '6px',
                backgroundColor: 'transparent', border: 'none', cursor: 'pointer',
                color: '#9ca3af', fontSize: '11px', fontWeight: '600', letterSpacing: '0.08em', textTransform: 'uppercase',
                padding: 0
              }}
            >
              <BookOpen size={12} /> Rôles & Prompts
              <span style={{ marginLeft: 'auto' }}>
                {showRoles ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
              </span>
            </button>
            <button
              onClick={() => setShowNewSkillForm(!showNewSkillForm)}
              style={{
                background: 'none', border: 'none', color: '#9ca3af', cursor: 'pointer',
                fontSize: '16px', padding: '0', lineHeight: 1, transition: 'color 0.15s', marginLeft: '8px'
              }}
              onMouseEnter={e => e.currentTarget.style.color = '#a78bfa'}
              onMouseLeave={e => e.currentTarget.style.color = '#9ca3af'}
              title="Nouveau rôle/prompt"
            >
              <Plus size={14} />
            </button>
          </div>

          {showRoles && (
            <div style={{ padding: '0 8px 10px', display: 'flex', flexDirection: 'column', gap: '3px' }}>
              {/* Formulaire création skill */}
              {showNewSkillForm && (
                <div style={{ background: '#0f172a', border: '1px solid #374151', borderRadius: '8px', padding: '12px', marginBottom: '8px' }}>
                  <input
                    type="text"
                    autoComplete="off"
                    placeholder="Nom du rôle/prompt *"
                    value={newSkill.name}
                    onChange={e => setNewSkill({...newSkill, name: e.target.value})}
                    style={{
                      width: '100%', padding: '6px 8px', background: '#1f2937', border: '1px solid #374151',
                      borderRadius: '6px', color: 'white', fontSize: '12px', marginBottom: '8px', boxSizing: 'border-box' as const
                    }}
                  />
                  <textarea
                    placeholder="Contenu / instructions *"
                    value={newSkill.content}
                    onChange={e => setNewSkill({...newSkill, content: e.target.value})}
                    style={{
                      width: '100%', padding: '6px 8px', background: '#1f2937', border: '1px solid #374151',
                      borderRadius: '6px', color: 'white', fontSize: '12px', marginBottom: '8px', boxSizing: 'border-box' as const,
                      minHeight: '60px', resize: 'vertical', fontFamily: 'inherit'
                    }}
                  />
                  <div style={{ display: 'flex', gap: '6px' }}>
                    <button
                      onClick={handleCreateSkill}
                      disabled={!newSkill.name.trim() || !newSkill.content.trim()}
                      style={{
                        flex: 1, padding: '6px', background: (!newSkill.name.trim() || !newSkill.content.trim()) ? '#374151' : 'linear-gradient(to right, #a855f7, #8b5cf6)',
                        color: 'white', border: 'none', borderRadius: '6px', fontSize: '12px',
                        fontWeight: '600', cursor: (!newSkill.name.trim() || !newSkill.content.trim()) ? 'not-allowed' : 'pointer'
                      }}
                    >
                      ✓ Créer
                    </button>
                    <button
                      onClick={() => {
                        setShowNewSkillForm(false)
                        setNewSkill({ name: '', content: '' })
                      }}
                      style={{
                        padding: '6px 12px', background: '#374151', color: 'white', border: 'none',
                        borderRadius: '6px', fontSize: '12px', cursor: 'pointer'
                      }}
                    >
                      ✕
                    </button>
                  </div>
                </div>
              )}

              {/* Option "Aucun rôle" */}
              <button
                onClick={() => setSelectedSkillId(null)}
                style={{
                  width: '100%', textAlign: 'left', padding: '7px 10px', borderRadius: '6px',
                  border: '1px solid', cursor: 'pointer', fontSize: '12px', transition: 'all 0.15s',
                  backgroundColor: !selectedSkillId ? 'rgba(236,72,153,0.1)' : 'transparent',
                  borderColor: !selectedSkillId ? 'rgba(236,72,153,0.4)' : 'transparent',
                  color: !selectedSkillId ? '#ec4899' : '#9ca3af'
                }}
              >
                🎭 Aucun rôle
              </button>

              {skills.filter((s: any) => (s.project_id || 'general') === activeProjectId).length === 0 ? (
                <p style={{ color: '#4b5563', fontSize: '11px', padding: '6px 10px', margin: 0 }}>
                  Créez des rôles dans Prompts
                </p>
              ) : (
                skills.filter((s: any) => (s.project_id || 'general') === activeProjectId).map(skill => {
                  const isEditing = editingSkillId === skill.id
                  const isHovered = hoveredSkillId === skill.id
                  const isActive = selectedSkillId === skill.id

                  if (isEditing) {
                    return (
                      <div
                        key={skill.id}
                        style={{
                          background: '#0f172a', border: '1px solid #374151', borderRadius: '8px',
                          padding: '12px', marginBottom: '4px'
                        }}
                      >
                        <input
                          type="text"
                          autoComplete="off"
                          value={editingSkill.name}
                          onChange={e => setEditingSkill({...editingSkill, name: e.target.value})}
                          onKeyDown={e => {
                            if (e.key === 'Enter' && e.shiftKey === false) handleUpdateSkill(skill.id)
                            if (e.key === 'Escape') { setEditingSkillId(null); setEditingSkill({ name: '', content: '' }) }
                          }}
                          style={{
                            width: '100%', padding: '6px 8px', background: '#1f2937', border: '1px solid #374151',
                            borderRadius: '6px', color: 'white', fontSize: '12px', marginBottom: '8px', boxSizing: 'border-box' as const
                          }}
                          placeholder="Nom du rôle *"
                        />
                        <textarea
                          value={editingSkill.content}
                          onChange={e => setEditingSkill({...editingSkill, content: e.target.value})}
                          onKeyDown={e => {
                            if (e.key === 'Escape') { setEditingSkillId(null); setEditingSkill({ name: '', content: '' }) }
                          }}
                          style={{
                            width: '100%', padding: '6px 8px', background: '#1f2937', border: '1px solid #374151',
                            borderRadius: '6px', color: 'white', fontSize: '12px', marginBottom: '8px', boxSizing: 'border-box' as const,
                            minHeight: '60px', resize: 'vertical', fontFamily: 'inherit'
                          }}
                          placeholder="Contenu *"
                        />
                        <div style={{ display: 'flex', gap: '6px' }}>
                          <button
                            onClick={() => handleUpdateSkill(skill.id)}
                            disabled={!editingSkill.name.trim() || !editingSkill.content.trim()}
                            style={{
                              flex: 1, padding: '6px', background: (!editingSkill.name.trim() || !editingSkill.content.trim()) ? '#374151' : '#10b981',
                              color: 'white', border: 'none', borderRadius: '6px', fontSize: '12px',
                              fontWeight: '600', cursor: (!editingSkill.name.trim() || !editingSkill.content.trim()) ? 'not-allowed' : 'pointer'
                            }}
                          >
                            ✓ Modifier
                          </button>
                          <button
                            onClick={() => { setEditingSkillId(null); setEditingSkill({ name: '', content: '' }) }}
                            style={{
                              padding: '6px 12px', background: '#374151', color: 'white', border: 'none',
                              borderRadius: '6px', fontSize: '12px', cursor: 'pointer'
                            }}
                          >
                            ✕
                          </button>
                        </div>
                      </div>
                    )
                  }

                  return (
                    <div
                      key={skill.id}
                      onMouseEnter={() => setHoveredSkillId(skill.id)}
                      onMouseLeave={() => setHoveredSkillId(null)}
                      style={{ position: 'relative' }}
                    >
                      <button
                        onClick={() => setSelectedSkillId(selectedSkillId === skill.id ? null : skill.id)}
                        title={skill.description || skill.name}
                        style={{
                          width: '100%', textAlign: 'left', padding: '7px 10px', borderRadius: '6px',
                          border: '1px solid', cursor: 'pointer', fontSize: '12px', transition: 'all 0.15s',
                          backgroundColor: isActive ? 'rgba(139,92,246,0.15)' : (isHovered ? '#374151' : 'transparent'),
                          borderColor: isActive ? 'rgba(139,92,246,0.5)' : 'transparent',
                          color: isActive ? '#a78bfa' : (isHovered ? '#f3f4f6' : '#9ca3af'),
                          display: 'flex', alignItems: 'center', gap: '6px'
                        }}
                      >
                        <span style={{ fontSize: '14px' }}>✨</span>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontWeight: '500', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{skill.name}</div>
                          {skill.description && (
                            <div style={{ fontSize: '10px', opacity: 0.7, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', marginTop: '1px' }}>{skill.description}</div>
                          )}
                        </div>
                        {isHovered && (
                          <div style={{ display: 'flex', gap: '4px', marginLeft: 'auto' }} onClick={e => e.stopPropagation()}>
                            <button
                              onClick={(e) => startEditingSkill(e, skill)}
                              style={{
                                padding: '2px 6px', fontSize: '11px', background: 'transparent',
                                color: '#f59e0b', border: 'none', cursor: 'pointer', borderRadius: '4px'
                              }}
                              title="Modifier"
                            >
                              ✏️
                            </button>
                            <button
                              onClick={(e) => handleDeleteSkill(e, skill.id)}
                              style={{
                                padding: '2px 6px', fontSize: '11px', background: 'transparent',
                                color: '#ef4444', border: 'none', cursor: 'pointer', borderRadius: '4px'
                              }}
                              title="Supprimer"
                            >
                              🗑️
                            </button>
                          </div>
                        )}
                      </button>
                    </div>
                  )
                })
              )}
            </div>
          )}
        </div>

        {/* ── Conversations groupées ── */}
        <div style={{ padding: '10px 8px', flex: 1 }}>
          <div style={{ fontSize: '11px', color: '#6b7280', fontWeight: '600', letterSpacing: '0.08em', textTransform: 'uppercase', padding: '0 6px 8px', display: 'flex', alignItems: 'center', gap: '6px' }}>
            📅 HISTORIQUE
          </div>
          {conversations.filter((c: any) => (c.project_id || 'general') === activeProjectId).length === 0 ? (
            <p style={{ color: '#4b5563', textAlign: 'center', padding: '16px 0', fontSize: '13px', margin: 0 }}>Aucune conversation</p>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
              {Array.from(groups.values())
                .sort((a, b) => {
                  if (a.model === 'Ollama (Local)') return -1
                  if (b.model === 'Ollama (Local)') return 1
                  return b.conversations.length - a.conversations.length
                })
                .map(group => (
                  <div key={group.model}>
                    {/* Header groupe modèle */}
                    <button
                      onClick={() => toggleModelGroup(group.model)}
                      style={{
                        width: '100%', display: 'flex', alignItems: 'center', gap: '6px',
                        padding: '6px 10px', borderRadius: '6px', border: 'none', cursor: 'pointer',
                        backgroundColor: group.color + '22', color: group.color, fontSize: '12px',
                        fontWeight: '600', marginBottom: '2px', transition: 'all 0.15s'
                      }}
                    >
                      {expandedModels.has(group.model) ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                      <Cpu size={12} />
                      <span style={{ flex: 1, textAlign: 'left', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{group.model}</span>
                      <span style={{ backgroundColor: group.color + '33', padding: '1px 6px', borderRadius: '10px', fontSize: '11px' }}>{group.conversations.length}</span>
                    </button>

                    {/* Conversations du groupe */}
                    {expandedModels.has(group.model) && (
                      <div style={{ paddingLeft: '8px', display: 'flex', flexDirection: 'column', gap: '2px' }}>
                        {group.conversations.map(conv => {
                          const isActive = currentConversation?.id === conv.id
                          return (
                            <button
                              key={conv.id}
                              onClick={() => selectConversation(conv.id)}
                              style={{
                                width: '100%', textAlign: 'left', padding: '8px 10px', borderRadius: '6px',
                                border: '1px solid', cursor: 'pointer', fontSize: '12px', transition: 'all 0.15s',
                                backgroundColor: isActive ? 'rgba(236,72,153,0.15)' : 'transparent',
                                borderColor: isActive ? 'rgba(236,72,153,0.4)' : 'transparent',
                                color: isActive ? '#ffffff' : '#d1d5db'
                              }}
                              onMouseEnter={e => { if (!isActive) { e.currentTarget.style.backgroundColor = '#374151'; e.currentTarget.style.borderColor = '#4b5563' } }}
                              onMouseLeave={e => { if (!isActive) { e.currentTarget.style.backgroundColor = 'transparent'; e.currentTarget.style.borderColor = 'transparent' } }}
                            >
                              <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '4px' }}>
                                <div style={{ flex: 1, minWidth: 0 }}>
                                  <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontWeight: '500' }}>{conv.title}</div>
                                  <div style={{ fontSize: '10px', color: '#6b7280', marginTop: '2px' }}>
                                    {conv.message_count} messages · {new Date(conv.created_at).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit' })}
                                  </div>
                                </div>
                                <button
                                  onClick={(e) => handleDeleteConversation(e, conv.id)}
                                  style={{ opacity: 0, padding: '2px', backgroundColor: 'transparent', border: 'none', cursor: 'pointer', color: '#ef4444', borderRadius: '4px', flexShrink: 0 }}
                                  onMouseEnter={e => { e.currentTarget.style.opacity = '1'; e.currentTarget.style.backgroundColor = '#7f1d1d' }}
                                  onMouseLeave={e => { e.currentTarget.style.opacity = '0' }}
                                >
                                  <Trash2 size={12} />
                                </button>
                              </div>
                            </button>
                          )
                        })}
                      </div>
                    )}
                  </div>
                ))
              }
            </div>
          )}
        </div>
      </div>

      {/* ── Footer : sélecteur provider ── */}
      <div style={{ borderTop: '1px solid #374151', padding: '12px 14px', flexShrink: 0 }}>
        <label style={{ fontSize: '11px', color: '#6b7280', fontWeight: '600', letterSpacing: '0.06em', textTransform: 'uppercase', display: 'block', marginBottom: '6px' }}>
          Modèle actif
        </label>
        <select
          value={selectedProviderId || ''}
          onChange={(e) => setSelectedProviderId(e.target.value)}
          style={{ width: '100%', backgroundColor: '#111827', border: '1px solid #374151', color: '#f3f4f6', padding: '7px 8px', borderRadius: '6px', fontSize: '12px', outline: 'none', cursor: 'pointer' }}
        >
          <option value="">Auto</option>
          {providers.map(p => (
            <option key={p.id} value={p.id}>{p.name}</option>
          ))}
        </select>
      </div>
    </aside>
  )
}
